import { auth } from './firebase';

export const GUARDRAILS_URL = 'https://bidsense-guardrails-314730656891.europe-west2.run.app';

/**
 * The reporting service, which is a different deployment from guardrails and
 * deliberately so.
 *
 * Guardrails holds the database credential, the Meta tokens and Secret
 * Manager access, because governance needs them. Reporting needs none of that
 * and must not have it: it runs as a service account whose entire reach is
 * seven views of one client's warehouse data, proven by impersonating it
 * rather than by reading an access list. Two deployments is the price of that
 * boundary.
 *
 * Both are called with the same Firebase token and the same error handling,
 * so from this file the only difference is the host.
 */
export const REPORTING_URL = 'https://bidsense-reports-ow3xf76rhq-nw.a.run.app';

/**
 * Thrown when the caller is not signed in, the session has expired, or the
 * signed-in account is not on the server-side allowlist. Pages catch this
 * separately so they can prompt a re-sign-in rather than showing a generic
 * "something went wrong".
 */
export class AuthError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Returns an Authorization header carrying a current Firebase ID token.
 * getIdToken() refreshes automatically when the cached token is near expiry,
 * so this is safe to call before every request — tokens last an hour and a
 * long dashboard session would otherwise start failing silently.
 */
async function authHeader() {
  const user = auth.currentUser;
  if (!user) {
    throw new AuthError('You are not signed in.');
  }
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function handleResponse(response) {
  if (response.status === 401) {
    throw new AuthError('Your session has expired. Please sign in again.');
  }
  if (response.status === 403) {
    throw new AuthError('This account is not authorised to access BidSense.');
  }
  if (!response.ok) {
    let detail = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body.detail) detail = body.detail;
    } catch (e) {
      // response had no JSON body; keep the status-code message
    }
    throw new Error(detail);
  }
  return response.json();
}

export async function apiGet(path) {
  const response = await fetch(`${GUARDRAILS_URL}${path}`, {
    headers: await authHeader(),
  });
  return handleResponse(response);
}

export async function apiPatch(path, body) {
  const response = await fetch(`${GUARDRAILS_URL}${path}`, {
    method: 'PATCH',
    headers: {
      ...(await authHeader()),
      'Content-Type': 'application/json',
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handleResponse(response);
}

/**
 * Multipart upload. Deliberately does not set Content-Type — the browser must
 * generate it so it can include the multipart boundary.
 */
export async function apiPostForm(path, formData) {
  const response = await fetch(`${GUARDRAILS_URL}${path}`, {
    method: 'POST',
    headers: await authHeader(),
    body: formData,
  });
  return handleResponse(response);
}
export async function apiPut(path, body) {
  const response = await fetch(`${GUARDRAILS_URL}${path}`, {
    method: 'PUT',
    headers: {
      ...(await authHeader()),
      'Content-Type': 'application/json',
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handleResponse(response);
}

export async function apiDelete(path) {
  const response = await fetch(`${GUARDRAILS_URL}${path}`, {
    method: 'DELETE',
    headers: await authHeader(),
  });
  return handleResponse(response);
}

export async function apiPost(path, body) {
  const response = await fetch(`${GUARDRAILS_URL}${path}`, {
    method: 'POST',
    headers: {
      ...(await authHeader()),
      'Content-Type': 'application/json',
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handleResponse(response);
}

/* ------------------------------------------------------------------ */
/* The reporting service                                               */
/*                                                                     */
/* Same token, same AuthError, same `detail` handling — only the host  */
/* differs. Separate functions rather than a host argument on the ones */
/* above, so that a call site cannot accidentally send a governance    */
/* request to the reporting service or the other way round.            */
/* ------------------------------------------------------------------ */

export async function reportingGet(path) {
  const response = await fetch(`${REPORTING_URL}${path}`, {
    headers: await authHeader(),
  });
  return handleResponse(response);
}

export async function reportingPost(path, body) {
  const response = await fetch(`${REPORTING_URL}${path}`, {
    method: 'POST',
    headers: {
      ...(await authHeader()),
      'Content-Type': 'application/json',
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handleResponse(response);
}
